import { Mentions, Select, Tag, Typography } from "antd";
import { useMemo, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

/** One field the agent offers at a point (ADR-0071), as the console's data map lists it. */
export interface PickerField {
  /** State path without `state.` (`request.pages`). */
  path: string;
  /** Plain name: the value, then the field ("The request › Pages to open"). */
  label: string;
  /** What kind of data, in words ("a list", "text"). */
  typeText?: string | null;
  description?: string | null;
  /** Values it can take, when known. */
  known?: readonly unknown[] | null;
  /** Empty on some paths to this point. */
  maybe?: boolean;
  /** Made by ("step 2 · Find the city's clubs"). */
  source?: string | null;
  depth?: number;
}

function FieldOption({ f, maybeText }: { f: PickerField; maybeText: string }) {
  return (
    <div className="ap-data-option" data-path={f.path}>
      <div className="ap-data-option-head">
        <span dir="auto" style={{ paddingInlineStart: (f.depth ?? 0) * 10 }}>
          {f.label}
        </span>
        <span className="ap-data-option-tags">
          {f.typeText ? <Tag bordered={false}>{f.typeText}</Tag> : null}
          {f.maybe ? (
            <Tag bordered={false} color="orange">
              {maybeText}
            </Tag>
          ) : null}
        </span>
      </div>
      <div className="ap-data-option-meta">
        <code dir="ltr">{f.path}</code>
        {f.source ? <span dir="auto"> · {f.source}</span> : null}
      </div>
      {f.description ? (
        <Typography.Text type="secondary" style={{ fontSize: 12 }} dir="auto">
          {f.description}
        </Typography.Text>
      ) : null}
      {f.known?.length ? (
        <div className="ap-data-option-meta" dir="ltr">
          {f.known.slice(0, 8).map((k) => JSON.stringify(k)).join(" · ")}
        </div>
      ) : null}
    </div>
  );
}

const searchText = (f: PickerField) => `${f.label} ${f.path} ${f.description ?? ""} ${f.source ?? ""}`.toLowerCase();

export interface DataPickerProps {
  fields: readonly PickerField[];
  value: string | undefined;
  onChange: (path: string | undefined) => void;
  placeholder?: string;
  id?: string;
  "aria-label"?: string;
  /** Lets the builder keep a path the map doesn't know (shown as is). */
  allowFree?: boolean;
  style?: React.CSSProperties;
  "data-testid"?: string;
}

/**
 * Pick a value from earlier steps (ADR-0071): every field with its plain name, type, where it
 * comes from, its known values and whether it may be empty on some path. Searchable by name or
 * path; typing a path the map does not know keeps it when `allowFree`.
 */
export function DataPicker({ fields, value, onChange, placeholder, allowFree = true, style, ...rest }: DataPickerProps) {
  const { t } = useTranslation("graphClear");
  const maybeText = t("data.maybe");
  const options = useMemo(() => {
    const list = fields.map((f) => ({ value: f.path, label: <FieldOption f={f} maybeText={maybeText} />, search: searchText(f), title: f.label }));
    if (value && !fields.some((f) => f.path === value)) list.unshift({ value, label: <FieldOption f={{ path: value, label: value }} maybeText={maybeText} />, search: value, title: value });
    return list;
  }, [fields, value, maybeText]);
  const chosen = fields.find((f) => f.path === value);
  return (
    <Select
      showSearch
      allowClear
      value={value}
      placeholder={placeholder}
      style={{ minInlineSize: 220, ...style }}
      popupMatchSelectWidth={false}
      listHeight={360}
      options={options}
      optionLabelProp="title"
      labelRender={() => (chosen ? chosen.label : value)}
      filterOption={(input, o) => String((o as { search?: string })?.search ?? "").includes(input.toLowerCase())}
      onChange={(v?: string) => onChange(v || undefined)}
      onSearch={undefined}
      notFoundContent={allowFree ? t("data.typePath") : t("data.none")}
      onInputKeyDown={(e) => {
        const input = e.currentTarget as HTMLInputElement;
        if (allowFree && e.key === "Enter" && /^[A-Za-z_][\w.[\]]*$/.test(input.value) && !fields.some((f) => f.path === input.value)) onChange(input.value);
      }}
      {...rest}
    />
  );
}

export type TemplateSyntax = "mustache" | "brace" | "state" | "prompt";

/** A piece of template text: plain text, or a value placeholder (`path` without `state.`). */
export type TemplatePart = { text: string } | { path: string; raw: string };

const PATH = "[A-Za-z_]\\w*(?:\\.[A-Za-z_]\\w*|\\[\\d+\\])*";
const TOKEN: Record<TemplateSyntax, string> = {
  // Prompts read a value or a field inside it: {question}, {trigger.order_id} (G-io-03).
  brace: `(?<!\\{)\\{(${PATH})\\}(?!\\})`,
  mustache: `\\{\\{\\s*state\\.(${PATH})\\s*\\}\\}`,
  prompt: `\\{\\{\\s*(${PATH})\\s*\\}\\}`,
  state: `\\bstate\\.(${PATH})`,
};

/** Splits template text into plain text and the values it inserts, in the syntax given. */
export function templateParts(text: string, syntax: TemplateSyntax): TemplatePart[] {
  const out: TemplatePart[] = [];
  let at = 0;
  for (const m of text.matchAll(new RegExp(TOKEN[syntax], "g"))) {
    const i = m.index ?? 0;
    if (i > at) out.push({ text: text.slice(at, i) });
    out.push({ path: m[1] as string, raw: m[0] });
    at = i + m[0].length;
  }
  if (at < text.length) out.push({ text: text.slice(at) });
  return out;
}

/** The placeholder for a value in a syntax: `{x}`, `{{ state.x }}`, `{{x}}` or `state.x`. */
export function templateToken(path: string, syntax: TemplateSyntax): string {
  return syntax === "brace" ? `{${path}}` : syntax === "mustache" ? `{{ state.${path} }}` : syntax === "prompt" ? `{{${path}}}` : `state.${path}`;
}

/** Inserts `token` at `pos` (end when out of range), with a space where it would touch a word. */
export function insertToken(text: string, pos: number | null | undefined, token: string): { text: string; caret: number } {
  const at = pos === null || pos === undefined || pos < 0 || pos > text.length ? text.length : pos;
  const before = text.slice(0, at);
  const after = text.slice(at);
  const pre = before && !/\s$/.test(before) ? " " : "";
  const post = after && !/^\s/.test(after) ? " " : "";
  const next = `${before}${pre}${token}${post}${after}`;
  return { text: next, caret: before.length + pre.length + token.length };
}

export interface TemplateInputProps {
  fields: readonly PickerField[];
  value: string;
  onChange: (v: string) => void;
  /** `{{ state.x }}` (tool arguments, queries), `{x}` (prompts), `state.x` (conditions). */
  syntax: TemplateSyntax;
  id?: string;
  "aria-label"?: string;
  "aria-describedby"?: string;
  rows?: number;
  placeholder?: string;
  dir?: "ltr" | "rtl" | "auto";
  "data-testid"?: string;
  footer?: ReactNode;
  /**
   * Plain-names mode (Builder step Edit): a preview of the text with each placeholder shown by its
   * plain name, and an "Insert a value" list that puts the right placeholder at the cursor. The raw
   * text stays editable above it.
   */
  plain?: boolean;
  /** Values the platform fills itself (`today`, …) with their plain names, for the preview. */
  builtIns?: readonly PickerField[];
  /** The field's name, for the insert list's accessible name. */
  fieldName?: string;
}

const PREFIX: Record<TemplateSyntax, string> = { mustache: "{{", brace: "{", state: "state.", prompt: "{{" };
/** Saved prompts (`{{x}}`) take whole values only; every other syntax reaches fields inside them. */
const topLevel = (syntax: TemplateSyntax) => (f: PickerField) => syntax !== "prompt" || (!f.path.includes(".") && !f.path.includes("["));

/** The text with each placeholder as a small tag carrying its plain name (unknown ones marked). */
export function TemplatePreview({ value, syntax, fields, "data-testid": testId }: { value: string; syntax: TemplateSyntax; fields: readonly PickerField[]; "data-testid"?: string }) {
  const { t } = useTranslation("graphClear");
  const parts = templateParts(value, syntax);
  if (!parts.some((x) => "path" in x)) return null;
  return (
    <div className="ap-template-preview" data-testid={testId}>
      <Typography.Text type="secondary" style={{ fontSize: 12, display: "block" }}>
        {t("data.plainPreview")}
      </Typography.Text>
      <div dir="auto" className="ap-template-preview-text">
        {parts.map((x, i) => {
          if ("text" in x) return <span key={i}>{x.text}</span>;
          const f = fields.find((y) => y.path === x.path);
          return f ? (
            <Tag key={i} bordered={false} color="blue" className="ap-template-chip" title={x.raw} data-path={x.path}>
              {f.label}
            </Tag>
          ) : (
            <Tag key={i} bordered={false} color="orange" className="ap-template-chip" title={x.raw} data-path={x.path}>
              <span dir="ltr">{x.path}</span> · {t("data.unknownValue")}
            </Tag>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Text with data autocomplete (ADR-0071): typing `{{` (or `{` in prompts, `state.` in raw
 * conditions) lists the fields available here; choosing one inserts it in the right syntax.
 */
/**
 * `{{x}}` typed by hand for a value the agent has becomes `{{ state.x }}`, the form the platform
 * reads (G-camp-cs-14; the same rule as `fixTemplate` in @ap/api).
 */
export function completeMustache(text: string, fields: readonly { path: string }[]): string {
  if (!text.includes("{{")) return text;
  const roots = new Set(fields.map((f) => f.path.split(/[.[]/)[0]));
  return text.replace(/\{\{\s*([A-Za-z_]\w*)((?:\.[A-Za-z_]\w*|\[\d+\])*)\s*\}\}/g, (whole, root: string, rest: string) => (root !== "state" && roots.has(root) ? `{{ state.${root}${rest} }}` : whole));
}

export function TemplateInput({ fields, value, onChange: change, syntax, rows = 2, footer, plain = false, builtIns = [], fieldName, ...rest }: TemplateInputProps) {
  const onChange = (text: string) => change(syntax === "mustache" ? completeMustache(text, fields) : text);
  const { t } = useTranslation("graphClear");
  const ref = useRef<{ textarea?: HTMLTextAreaElement | null } | null>(null);
  // The insert list is a one-shot action: it is remounted empty after each choice.
  const [picks, setPicks] = useState(0);
  const usable = useMemo(() => fields.filter(topLevel(syntax)), [fields, syntax]);
  const options = useMemo(
    () =>
      usable.map((f) => ({
        value: syntax === "mustache" ? ` state.${f.path} }}` : syntax === "brace" ? `${f.path}}` : syntax === "prompt" ? `${f.path}}}` : f.path,
        key: f.path,
        label: <FieldOption f={f} maybeText={t("data.maybe")} />,
        search: searchText(f),
      })),
    [usable, syntax, t],
  );
  const known = useMemo(() => [...usable, ...builtIns], [usable, builtIns]);
  const insert = (path: string | undefined) => {
    if (!path) return;
    const area = ref.current?.textarea ?? null;
    const { text, caret } = insertToken(value, area ? area.selectionStart : null, templateToken(path, syntax));
    onChange(text);
    setPicks((n) => n + 1);
    requestAnimationFrame(() => {
      const el = ref.current?.textarea;
      if (el) {
        el.focus();
        el.setSelectionRange(caret, caret);
      }
    });
  };
  return (
    <>
      <Mentions
        ref={ref as never}
        prefix={PREFIX[syntax]}
        value={value}
        onChange={onChange}
        autoSize={{ minRows: rows, maxRows: 10 }}
        options={options}
        split=""
        filterOption={(input, o) => String((o as { search?: string })?.search ?? "").includes(input.toLowerCase())}
        popupClassName="ap-data-mentions"
        className="ap-mono"
        {...rest}
      />
      {plain ? (
        <div className="ap-template-tools">
          <TemplatePreview value={value} syntax={syntax} fields={known} data-testid={rest["data-testid"] ? `${rest["data-testid"]}-preview` : undefined} />
          <DataPicker
            key={picks}
            fields={known}
            value={undefined}
            onChange={insert}
            allowFree={false}
            placeholder={t("data.insert")}
            aria-label={fieldName ? t("data.insertFor", { field: fieldName }) : t("data.insert")}
            style={{ inlineSize: "100%" }}
            data-testid={rest["data-testid"] ? `${rest["data-testid"]}-insert` : undefined}
          />
        </div>
      ) : null}
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        {t(`data.hint.${syntax}`)}
      </Typography.Text>
      {footer}
    </>
  );
}
